import { useEffect, useState, useMemo, useCallback } from "react";
import type { SortingState } from "@tanstack/react-table";
import { useQuery, useMutation } from "@apollo/client";
import Table, {
  TableHeading,
  TableControls,
  TableFooter,
  buildBulkSelectionColumn,
  buildActionColumn
} from "@/components/Table";
import SearchInput from "@/components/SearchInput";
import SegmentedButton from "@/components/SegmentedButton/SegmentedButton";
import SideSheetModal from "@/components/SideSheetModal";
import SubSectionHeading from "@/components/SubSectionHeading";
import Button from "@/components/Button";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import EmptyState from "@/components/EmptyState";
import { toFormat } from "@/lib/dateHelpers";
import { showErrorSnackbar } from "@/lib/snackbarUtils";

import useActivityTypePermissions from "@/hooks/useActivityTypePermissions";
import { useHasRoles } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { useEventsBasePath } from "@/pages/events/_eventsBasePathContext";
import useIsSectionScopedForEvents from "@/pages/events/useIsSectionScopedForEvents";
import { useAttendanceEventQueryDocument } from "@/pages/events/[eventSlug]/useEventDetailQuery";
import { UPDATE_EVENT_SESSIONS_REGISTRATION_ATTENDANCES_MUTATION } from "../queries";
import { getEventTypeName } from "../../../utils";
import { getColumns } from "./TableColumns";
import { exportAttendanceList } from "./exportAttendanceList";
import SessionStepper from "./SessionStepper";
import type { AttendanceTableProps, EventWrapper, SessionRegistrationRow } from "../types";
import { useNavigate } from "@/router";
import type { Path } from "@/router";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

export default function AttendanceTable({ eventSlug, multiSession }: AttendanceTableProps) {
  const navigate = useNavigate();
  const basePath = useEventsBasePath();
  const { trackEvent } = useAmplitude();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const { rowSelection, setRowSelection, bulkSelectionProps, isSelectedOnly, deselectAll, rowSelectionCount } =
    useBulkSelection();
  const { setMenu, Menu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const [editingRow, setEditingRow] = useState<SessionRegistrationRow | null>(null);
  const [sortingState, setSortingState] = useState<SortingState>([]);

  const eventDetailQuery = useAttendanceEventQueryDocument();

  const { data: eventDataResponse, loading } = useQuery<EventWrapper>(eventDetailQuery, {
    variables: { slug: eventSlug }
  });
  const eventData = eventDataResponse?.event;

  const isSeminar = getEventTypeName(eventData)?.toLowerCase().includes("seminar");
  const isSectionScoped = useIsSectionScopedForEvents();
  const isEducationStaff = useHasRoles([ROLE.EDUCATION_STAFF, ROLE.GLOBAL_ADMIN]);
  const canAccessActivityManagement =
    useActivityTypePermissions(eventData?.activityType ?? { title: getEventTypeName(eventData) }) &&
    (!isSectionScoped || !!eventData?.section?.permissions?.manageEvents) &&
    !eventData?.finalizedAt;

  const [activeSession, setActiveSession] = useState<number>(0);

  const sessionRegistrations = useMemo(() => {
    if (!eventData?.sessions) return {};
    return eventData.sessions.reduce(
      (acc, session) => {
        acc[session.id] = session.registrations.filter(({ registration }) => !registration.canceledAt);
        return acc;
      },
      {} as Record<string, SessionRegistrationRow[]>
    );
  }, [eventData]);

  const [optimisticRegistrations, setOptimisticRegistrations] = useState<Record<string, SessionRegistrationRow[]>>({});

  useEffect(() => {
    setOptimisticRegistrations(sessionRegistrations);
  }, [sessionRegistrations]);

  const sessions = useMemo(() => {
    return eventData?.sessions.map((session) => ({
      id: session.id,
      startsAt: session.startsAt,
      endsAt: session.endsAt,
      registrations: session.registrations
    }));
  }, [eventData]);

  const sessionDates = useMemo(() => {
    return sessions?.map((session) => ({ id: session.id, startsAt: session.startsAt }));
  }, [sessions]);

  const currentSessionDate = sessionDates?.[activeSession]?.startsAt;
  const formattedSessionDate = currentSessionDate ? (toFormat(currentSessionDate) ?? "") : "";

  const hasRegistrations = Object.values(optimisticRegistrations).some((regs) => regs.length > 0);

  const activeSessionRegistrations = useMemo(() => {
    const currentSession = sessions?.[activeSession];
    return currentSession ? (optimisticRegistrations[currentSession.id] ?? []) : [];
  }, [activeSession, optimisticRegistrations, sessions]);

  const filteredData = useMemo(() => {
    const search = debouncedSearch?.toLowerCase() || "";
    if (!search) return activeSessionRegistrations;
    return activeSessionRegistrations.filter(
      (registrant) =>
        registrant.registration.person.displayName.toLowerCase().includes(search) ||
        registrant.registration.person.pgaId.toLowerCase().includes(search)
    );
  }, [activeSessionRegistrations, debouncedSearch]);

  const displayedData = useMemo(() => {
    if (isSelectedOnly) return filteredData.filter((registrant) => rowSelection[registrant.id]);
    return filteredData;
  }, [filteredData, isSelectedOnly, rowSelection]);

  const [updateEventSessionsRegistrationAttendances] = useMutation(
    UPDATE_EVENT_SESSIONS_REGISTRATION_ATTENDANCES_MUTATION
  );

  const handleAttendanceUpdate = useCallback(
    async (id: string, value: string | null) => {
      const currentSession = sessions?.[activeSession];
      if (!currentSession) return;

      const attended = value === "true" ? true : value === "false" ? false : null;
      let rollback: Record<string, SessionRegistrationRow[]> | null = null;

      setOptimisticRegistrations((prev) => {
        rollback = prev;
        return {
          ...prev,
          [currentSession.id]: prev[currentSession.id].map((reg) => (reg.id === id ? { ...reg, attended } : reg))
        };
      });

      try {
        const { data } = await updateEventSessionsRegistrationAttendances({
          variables: { attendances: [{ eventSessionRegistrationId: id, attended }] }
        });
        if (data?.updateEventSessionsRegistrationAttendances?.success) {
          trackEvent(
            EVENT_ANALYTICS_EVENTS.attendanceUpdated,
            getEventAnalyticsData({
              source: "attendance",
              eventId: eventData?.id,
              eventSlug,
              action: "update",
              count: 1,
              ...(attended !== null ? { attended } : {})
            })
          );
        }
      } catch {
        if (rollback) setOptimisticRegistrations(rollback);
        showErrorSnackbar("Failed to update attendance.");
      }
    },
    [sessions, activeSession, updateEventSessionsRegistrationAttendances, trackEvent, eventData?.id, eventSlug]
  );

  const handleBulkAttendanceUpdate = useCallback(
    async (present: boolean) => {
      const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
      if (selectedIds.length === 0) return;

      const currentSession = sessions?.[activeSession];
      if (!currentSession) return;

      let rollback: Record<string, SessionRegistrationRow[]> | null = null;

      setOptimisticRegistrations((prev) => {
        rollback = prev;
        return {
          ...prev,
          [currentSession.id]: prev[currentSession.id].map((reg) =>
            selectedIds.includes(reg.id) ? { ...reg, attended: present } : reg
          )
        };
      });

      try {
        const { data } = await updateEventSessionsRegistrationAttendances({
          variables: {
            attendances: selectedIds.map((id) => ({ eventSessionRegistrationId: id, attended: present }))
          }
        });
        if (data?.updateEventSessionsRegistrationAttendances?.success) {
          trackEvent(
            EVENT_ANALYTICS_EVENTS.attendanceUpdated,
            getEventAnalyticsData({
              source: "attendance",
              eventId: eventData?.id,
              eventSlug,
              action: "bulk_update",
              count: selectedIds.length,
              attended: present
            })
          );
        }
      } catch {
        if (rollback) setOptimisticRegistrations(rollback);
        showErrorSnackbar("Failed to update attendance.");
      }
    },
    [
      rowSelection,
      sessions,
      activeSession,
      updateEventSessionsRegistrationAttendances,
      trackEvent,
      eventData?.id,
      eventSlug
    ]
  );

  const bulkActions = canAccessActivityManagement
    ? [
        {
          label: "Mark as Present",
          icon: "person_check" as const,
          variant: "outlined" as const,
          onClick: () => handleBulkAttendanceUpdate(true)
        },
        {
          label: "Mark as Absent",
          icon: "person_cancel" as const,
          variant: "outlined" as const,
          onClick: () => handleBulkAttendanceUpdate(false)
        }
      ]
    : [];

  const columns = useMemo(
    () => [
      buildBulkSelectionColumn<SessionRegistrationRow>(),
      ...getColumns({ onAttendanceUpdate: handleAttendanceUpdate, canAccessActivityManagement, isCompactLayout }),
      ...(isCompactLayout && canAccessActivityManagement
        ? [
            buildActionColumn<SessionRegistrationRow>({
              onEditClick: (row) => setEditingRow(row),
              editButtonTitle: "Edit Attendance",
              actionId,
              setMenu,
              getRowId: (row) => row.id
            })
          ]
        : [])
    ],
    [handleAttendanceUpdate, canAccessActivityManagement, isCompactLayout, setMenu]
  );

  const handleEditSheetChange = (value: string | null) => {
    if (!editingRow) return;
    const attended = value === "true" ? true : value === "false" ? false : null;
    setEditingRow({ ...editingRow, attended });
    handleAttendanceUpdate(editingRow.id, value);
  };

  return (
    <>
      <Menu />
      <div className="flex flex-col lg:flex-row">
        {multiSession && (
          <div className="sm:pt-12 pt-8 w-full lg:w-[240px]">
            <SessionStepper
              sessionDates={sessionDates || []}
              activeSession={activeSession}
              onSessionChange={setActiveSession}
            />
          </div>
        )}
        <div className="flex-1">
          <TableHeading
            heading={`Session Attendance for ${formattedSessionDate}`}
            body="Track each registrant's attendance for this session"
            actions={
              hasRegistrations
                ? [
                    ...(isSeminar && isEducationStaff && !eventData?.finalizedAt
                      ? [
                          {
                            label: "Finalize Attendance",
                            variant: "outlined" as const,
                            onClick: () => navigate(`${basePath}/${eventSlug}/attendance/finalize` as Path)
                          }
                        ]
                      : []),
                    {
                      label: "Export Attendance List",
                      icon: "download",
                      variant: "text",
                      onClick: () =>
                        exportAttendanceList(eventData?.title ?? "", sessions ?? [], optimisticRegistrations)
                    }
                  ]
                : []
            }
          />
          <Table
            data={displayedData}
            loading={loading}
            columns={columns}
            enableRowSelection
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            sortingState={sortingState}
            onSortingChange={setSortingState}
            hasActiveFilters={!!debouncedSearch}
            renderNoDataEmptyState={() => (
              <EmptyState
                title="No Registrants"
                caption="This event does not have any registrants."
                iconName="person"
              />
            )}
            renderFilteredEmptyState={() => (
              <EmptyState title="No Results" caption="Try a different search." iconName="search" />
            )}
            renderControls={(table, { onSortClick } = {}) => (
              <TableControls
                onSortClick={onSortClick}
                bulkActions={bulkActions}
                {...bulkSelectionProps}
                onDeselectAll={deselectAll}
                totalRowCount={rowSelectionCount}
                onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
                allRowsSelected={table.getIsAllRowsSelected()}
                someRowsSelected={table.getIsSomeRowsSelected()}
              >
                <SearchInput
                  query={searchValue}
                  onChange={setSearchValue}
                  onClear={clearSearch}
                  placeholder="Search Registrants"
                />
              </TableControls>
            )}
            renderFooter={() => <TableFooter totalRowCount={displayedData.length} {...bulkSelectionProps} />}
          />
        </div>
      </div>
      <SideSheetModal
        isSidebarOpen={!!editingRow}
        setSidebarOpen={(open) => {
          if (!open) setEditingRow(null);
        }}
        header={{
          title: editingRow?.registration.person.displayName ?? "Edit Attendance"
        }}
        actions={
          <Button variant="outlined" onClick={() => setEditingRow(null)}>
            Done
          </Button>
        }
        disableFocusManagement
      >
        {editingRow && (
          <div className="flex flex-col gap-6 pt-4">
            <div>
              <SubSectionHeading className="mb-2">Session Attendance for {formattedSessionDate}</SubSectionHeading>
              <SegmentedButton
                showCheckmark
                allowDeselect
                options={[
                  { label: "Present", value: "true" },
                  { label: "Absent", value: "false" }
                ]}
                value={editingRow.attended === null ? null : String(editingRow.attended)}
                onChange={handleEditSheetChange}
                disabled={!canAccessActivityManagement}
              />
            </div>
          </div>
        )}
      </SideSheetModal>
    </>
  );
}
